import {
  Button,
  Dialog,
  DialogContent,
  GenericError,
  Header,
  Illustration,
  LoadingSpinner,
  PuckRender,
  ReportContextProvider,
  ReportError,
  Text,
} from '@psyomics/ui-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useFetchReportData, UseFetchReportDataResponse } from './use-fetch-report-data';

type ReportProps = {
  readonly referralId: string;
  readonly referralSessionId?: string;
  readonly path?: string;
  readonly navigateToPath: (path: string) => void;
};
const SCROLL_DELAY = 100;

const useScrollLocationIntoView = () => {
  const location = useLocation();

  useEffect(() => {
    setTimeout(() => {
      if (location.hash) {
        const id = location.hash.replace('#', '');
        document.getElementById(id)?.scrollIntoView();
      }
    }, SCROLL_DELAY);
  }, [location]);
};

function QuestionnaireInProgressDialog({
  isOpen,
  setIsOpen,
}: {
  readonly isOpen: boolean;
  readonly setIsOpen: (open: boolean) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Dialog onOpenChange={setIsOpen} open={isOpen}>
      <DialogContent className="w-full sm:max-w-[725px] h-[447px] bg-neutral-100 rounded-2xl" showCloseButton={false}>
        <div className="flex justify-center items-start">
          <div className="flex flex-col gap-2 pl-[32px] pt-[64px]">
            <Header level="h1">{t('page.viewReferral.questionnaireInProgress.header')}</Header>
            <Text size="lg">{t('page.viewReferral.questionnaireInProgress.body')}</Text>
          </div>
          <div className="flex flex-col gap-2 pt-[32px] pr-[24px]">
            <Illustration className="shrink-0" name="WomanWip" size={300} />
            <Button className="w-[64px] self-end mr-[32px]" onClick={() => setIsOpen(false)}>
              {t('common.buttons.close')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

type ReportComponentViewProps = ReportProps &
  Omit<UseFetchReportDataResponse, 'fetching' | 'errorMessage'> &
  Pick<Required<UseFetchReportDataResponse>, 'reportData'>;
const ReportComponentView = ({
  fetchReportPdfUrl,
  navigateToPath,
  path,
  definition,
  surveys,
  reportKey,
  reports,
  referralSessionId,
  parsedAnswers,
  reportData,
}: ReportComponentViewProps) => {
  useScrollLocationIntoView();
  const [isOpen, setIsOpen] = useState(reportData.activeReferralSessionExists);

  return (
    <ReportContextProvider
      value={{
        exportPdf: fetchReportPdfUrl,
        id: referralSessionId ?? '',
        navigateToPath,
        path: path ?? '',
        reports: reports,
      }}
    >
      <PuckRender
        answers={parsedAnswers}
        healthcareLookupPerformed={reportData.healthcareLookupPerformed}
        healthcareSystemLookupId={reportData.healthcareSystemLookupId ?? undefined}
        healthcareSystemPatientId={reportData.healthcareSystemPatientId ?? undefined}
        key={reportKey}
        piSystemId={reportData.piSystemId}
        reportDefinition={definition}
        respondents={reportData.respondents ?? []}
        surveys={surveys}
      />
      <QuestionnaireInProgressDialog isOpen={isOpen} setIsOpen={setIsOpen} />
    </ReportContextProvider>
  );
};

const ReportComponent = (props: ReportProps) => {
  const { referralId, referralSessionId } = props;
  const { errorMessage, fetching, reportData, ...data } = useFetchReportData({
    referralId,
    referralSessionId,
  });
  if (fetching) return <LoadingSpinner />;
  if (errorMessage) {
    return errorMessage === 'ReportNotFound' ? (
      <GenericError titleKey={'page.report.errors.notFound'} />
    ) : (
      <ReportError error={errorMessage} />
    );
  }
  if (!reportData?.reportShapeDefinition) return <GenericError titleKey={'page.report.errors.notFound'} />;

  return (
    <div className="bg-gray-50">
      <ReportComponentView {...data} reportData={reportData} {...props} />;
    </div>
  );
};

export { ReportComponent as Report };
